import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CloudinaryService } from 'src/app/services/cloudinary/cloudinary.service';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { QR_CODE_ROUTE } from 'src/app/constants/route.constants';
import { Alert } from 'src/app/interfaces/alert';
import { Pet, PetGender } from 'src/app/interfaces/pet';
import { QrCodePreferences } from 'src/app/interfaces/qr-code';
import { User, UserAddress, UserAddressType } from 'src/app/interfaces/user';
import {
  ImageContent,
  ImageUploaderModalComponent,
} from 'src/app/modals/image-uploader-modal/image-uploader-modal.component';
import { QrCodeService } from 'src/app/services/qr-code/qr-code.service';
import { UserService } from 'src/app/services/user/user.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-qr-code-preferences-page',
  templateUrl: './qr-code-preferences-page.component.html',
  styleUrls: ['./qr-code-preferences-page.component.scss'],
})
export class QrCodePreferencesPageComponent implements OnInit {
  qrCodePreferences: QrCodePreferences;
  public user: User;
  public pet: Pet;
  loading: boolean = false;
  alert: Alert = null;
  userMainAddress: UserAddress;
  petImage: string;
  imageUploaderRef?: NgbModalRef;
  isBrowser: boolean;

  constructor(
    private route: ActivatedRoute,
    private qrCodeService: QrCodeService,
    private router: Router,
    private modalService: NgbModal,
    public userService: UserService,
    private cloudinaryService: CloudinaryService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.qrCodePreferences = this.route.snapshot.data
      .qrCodePreferences as QrCodePreferences;
    this.user = this.route.snapshot.data.user as User;
    this.pet = this.route.snapshot.data.pet as Pet;

    this.userMainAddress = this.getUserMainAddress();
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.petImage = this.getPetImageUrl(this.pet.imageId);
  }

  public getPetImageUrl(petImageId: string): string {
    return this.cloudinaryService.getImageUrl(petImageId, 360, 360, null);
  }

  getUserMainAddress(): UserAddress {
    return this.user.addresses.find(
      (a) => a.addressType === UserAddressType.Main
    );
  }

  navigateToQrCodeDataPage() {
    this.router.navigate([QR_CODE_ROUTE, this.pet.id], {
      queryParams: { preview: true },
    });
  }

  public getPetDateOfBirth() {
    if (this.pet.dob) {
      const day = new Date(this.pet.dob).getDate();
      const month = new Date(this.pet.dob).getMonth() + 1;
      const year = new Date(this.pet.dob).getFullYear();
      return `${day}/${month}/${year}`;
    }

    return '-';
  }

  public getPetGender() {
    switch (this.pet.gender) {
      case PetGender.Female:
        return 'Female';
      case PetGender.Male:
        return 'Male';
      default:
        return 'Unknown';
    }
  }

  public save() {
    this.loading = true;
    this.qrCodeService
      .saveQrCodePreferences(
        this.user.id,
        this.qrCodePreferences.petId,
        this.qrCodePreferences
      )
      .subscribe(
        () => {
          this.alert = {
            type: 'success',
            message: 'Your QR code information has been updated.',
          };
          this.navigateToQrCodeDataPage();
          this.loading = false;
        },
        (err) => {
          if (err.error?.error_message) {
            this.alert = {
              type: 'danger',
              message: err.error?.error_message,
            };
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
          this.loading = false;
        }
      );
  }

  updateImage(pet: Pet, imageContent: ImageContent): string {
    this.closeAlert();
    pet.imageId = imageContent.fileName;
    pet.imageUrl = imageContent.filePath;
    const petImage = this.getPetImageUrl(pet.imageId);
    this.alert = {
      type: 'success',
      message: 'Your pet image has been updated.',
    };
    // Update header cached pet picture without refreshing the page
    const headerPetData = this.userService.user.pets.find(
      (p) => p.id === pet.id
    );
    if (headerPetData) {
      headerPetData.imageId = pet.imageId;
      headerPetData.imageUrl = pet.imageUrl;
    }
    return petImage;
  }

  openImageUploader(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      size: 'md',
    };

    this.imageUploaderRef = this.modalService.open(
      ImageUploaderModalComponent,
      modalOptions
    );
    this.imageUploaderRef.componentInstance.title = `Edit ${this.pet.name}'s image`;
    this.imageUploaderRef.componentInstance.currentImageUrl = this.pet.imageUrl;
    this.imageUploaderRef.componentInstance.currentImageId = this.pet.imageId;
    this.imageUploaderRef.componentInstance.pet = this.pet;
    this.imageUploaderRef.result
      .then((res) => {
        const imageContent = res as ImageContent;
        this.petImage = this.updateImage(this.pet, imageContent);
        this.trackEvent('qrcodepreferencespage::image::uploaded');
      })
      .catch(() => {});
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  closeAlert() {
    this.alert = null;
  }
}
